import { useEffect, useMemo, useState } from "react";
import { ImagePlus, Loader2, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { COLLECTION_CATEGORIES, UNIVERSES, collectionLabel } from "@/data/catalog";
import { call, errText, getJson, mediaUrl } from "@/lib/api";
import { upload } from "./AdminCatalog";

const EMPTY = { category: "juguetes", title: "", year: "", number: "", universe: "", country: "", language: "", description: "" };
const TEXT_KEYS = ["universe", "description", "title", "country", "language", "number"];

const Fields = ({ v, set, prefix }) => (
  <>
    <select value={v.category} onChange={set("category")} className="support-input" data-testid={`${prefix}-category-select`}>
      {COLLECTION_CATEGORIES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
    </select>
    <input value={v.title ?? ""} onChange={set("title")} maxLength={120} placeholder="Título (opcional)" className="support-input" data-testid={`${prefix}-title-input`} />
    <input type="number" min={1950} max={2100} value={v.year ?? ""} onChange={set("year")} placeholder="Año, ej. 1991" className="support-input" data-testid={`${prefix}-year-input`} />
    <input value={v.number ?? ""} onChange={set("number")} maxLength={20} placeholder="Número (opcional)" className="support-input" data-testid={`${prefix}-number-input`} />
    <input value={v.country ?? ""} onChange={set("country")} maxLength={40} placeholder="País (opcional)" className="support-input" data-testid={`${prefix}-country-input`} />
    <input value={v.language ?? ""} onChange={set("language")} maxLength={40} placeholder="Idioma (opcional)" className="support-input" data-testid={`${prefix}-language-input`} />
    <input list="universes" value={v.universe ?? ""} onChange={set("universe")} maxLength={40} placeholder="Universo, ej. TMNT 2007" className="support-input sm:col-span-2" data-testid={`${prefix}-universe-input`} />
    <datalist id="universes">{UNIVERSES.map((u) => <option key={u} value={u} />)}</datalist>
    <textarea rows={3} maxLength={600} value={v.description ?? ""} onChange={set("description")} placeholder="Descripción, ej. En el castillo de Chapultepec" className="support-input sm:col-span-2" data-testid={`${prefix}-description-input`} />
  </>
);

const useForm = (init) => {
  const [v, setV] = useState(init);
  const set = (k) => (e) => setV((s) => ({ ...s, [k]: e.target.value }));
  return [v, setV, set];
};

const UploadForm = ({ onAdded }) => {
  const [v, setV, set] = useForm(EMPTY);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const preview = useMemo(() => (file ? URL.createObjectURL(file) : null), [file]);
  useEffect(() => {
    return () => { if (preview) URL.revokeObjectURL(preview); };
  }, [preview]);
  const submit = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Elige una foto");
    setBusy(true);
    try {
      onAdded(await upload("/admin/collection", file, v));
      setV(EMPTY);
      setFile(null);
      toast.success("Foto agregada a la colección");
    } catch (err) { toast.error(err.message); }
    setBusy(false);
  };
  return (
    <form onSubmit={submit} className="support-form grid gap-4 md:grid-cols-[220px_1fr]" data-testid="collection-upload-form">
      <label className="grid aspect-square cursor-pointer place-items-center overflow-hidden rounded-xl border border-dashed border-white/25 text-[var(--muted)] transition-colors hover:border-white/50" data-testid="collection-file-pick">
        {preview ? <img src={preview} alt="" className="h-full w-full object-cover" /> : <span className="flex flex-col items-center gap-2 text-sm"><ImagePlus size={22} /> Elegir foto</span>}
        <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={(e) => setFile(e.target.files?.[0] ?? null)} data-testid="collection-file-input" />
      </label>
      <div className="grid content-start gap-3 sm:grid-cols-2">
        <Fields v={v} set={set} prefix="collection" />
        <button type="submit" disabled={busy} className="btn-primary justify-self-start" data-testid="collection-upload-btn">{busy && <Loader2 size={16} className="animate-spin" />} Subir a la colección</button>
      </div>
    </form>
  );
};

const EditDialog = ({ photo, onClose, onSaved }) => {
  const [v, , set] = useForm({ ...EMPTY, ...photo });
  const [busy, setBusy] = useState(false);
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    const body = { category: v.category, year: v.year ? Number(v.year) : null, ...Object.fromEntries(TEXT_KEYS.map((k) => [k, v[k] ?? ""])) };
    const res = await call(`/admin/collection/${photo.id}`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
    const data = await res.json().catch(() => ({}));
    setBusy(false);
    if (!res.ok) return toast.error(errText(data?.detail));
    onSaved(data);
  };
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-xl border-white/10 bg-[#0d0c0a] text-[#efe8dc]" data-testid="collection-edit-dialog">
        <DialogTitle className="font-display text-3xl font-normal">Editar foto</DialogTitle>
        <img src={mediaUrl(photo.image_url)} alt="" className="max-h-56 w-full rounded-lg object-cover" />
        <form onSubmit={save} className="grid gap-3 sm:grid-cols-2">
          <Fields v={v} set={set} prefix="collection-edit" />
          <button type="submit" disabled={busy} className="btn-primary justify-self-start" data-testid="collection-edit-save">{busy && <Loader2 size={16} className="animate-spin" />} Guardar</button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export const AdminCollection = () => {
  const [rows, setRows] = useState(null);
  const [edit, setEdit] = useState(null);
  useEffect(() => { getJson("/collection").then((d) => setRows(Array.isArray(d) ? d : [])); }, []);
  const remove = async (p) => {
    if (!window.confirm("¿Quitar esta foto de la colección?")) return;
    const res = await call(`/admin/collection/${p.id}`, { method: "DELETE" });
    if (res.ok) setRows((s) => s.filter((x) => x.id !== p.id));
  };
  return (
    <div data-testid="admin-collection">
      <UploadForm onAdded={(p) => setRows((s) => [p, ...(s ?? [])])} />
      <p className="mono mt-8 text-[11px] text-[var(--muted)]" data-testid="admin-collection-count">{rows ? `${rows.length} fotos` : "Cargando…"}</p>
      <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
        {(rows ?? []).map((p) => (
          <div key={p.id} className="overflow-hidden rounded-xl border border-white/10" data-testid={`admin-collection-item-${p.id}`}>
            <img src={mediaUrl(p.image_url)} alt="" className="aspect-square w-full object-cover" loading="lazy" />
            <div className="flex items-start justify-between gap-2 p-2.5">
              <div className="min-w-0">
                <p className="truncate text-sm">{p.title || p.description || <span className="text-[var(--muted)]">Sin descripción</span>}</p>
                <p className="truncate text-xs text-[var(--muted)]">{[collectionLabel(p.category), p.year, p.universe].filter(Boolean).join(" · ")}</p>
              </div>
              <div className="flex shrink-0 gap-1">
                <button type="button" onClick={() => setEdit(p)} className="icon-btn !h-8 !w-8" aria-label="Editar" data-testid={`admin-collection-edit-${p.id}`}><Pencil size={14} /></button>
                <button type="button" onClick={() => remove(p)} className="icon-btn !h-8 !w-8" aria-label="Quitar" data-testid={`admin-collection-delete-${p.id}`}><Trash2 size={14} /></button>
              </div>
            </div>
          </div>
        ))}
      </div>
      {edit && <EditDialog key={edit.id} photo={edit} onClose={() => setEdit(null)} onSaved={(p) => { setRows((s) => s.map((x) => (x.id === p.id ? p : x))); setEdit(null); toast.success("Foto actualizada"); }} />}
    </div>
  );
};
